<template>
  <div
    class="chip-wrapper"
    :class="[
      {
        active: !displayOnly && chipValue === activeChip,
        disabled,
        displayOnly,
      },
    ]"
    @click.stop="updateActiveChipHandler"
  >
    <slot></slot>
    <span class="chip-value" :class="fontSizeStyle">
      {{ convertedChipValue }}
    </span>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { isMobile } from 'mobile-device-detect';
import { mathFloor } from '@/utils/helpers';

export default {
  props: {
    chipValue: {
      type: Number | String,
      default: 10,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    displayOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      activeChip: 'bets/activeChip',
      ratio: 'general/inputPanelScaleRatio',
    }),
    convertedChipValue() {
      return this.chipValue >= 10000 ? this.getShortChipValue : this.chipValue.toString();
    },
    getShortChipValue() {
      const unit = this.chipValue >= 1e6 ? 'M' : 'K';
      const tempVal = this.chipValue >= 1e6 ? this.chipValue / 1000 : this.chipValue;
      if (Number.isInteger(tempVal / 1000)) return `${(tempVal / 1000).toFixed(0)}${unit}`;
      else if (Number.isInteger(tempVal / 100)) return `${(mathFloor(tempVal / 100) / 10).toFixed(1)}${unit}`;
      else return `${(mathFloor(tempVal / 100) / 10).toFixed(1)}${unit}+`;
    },
    isMobileLandscape() {
      return isMobile && this.$screen.width > this.$screen.height;
    },
    scaleRatio() {
      // Falls back to 1 until the cockfight input panel reports its ratio --
      // otherwise dividing by 0 pins every chip to the maximum font size.
      const ratio = !isMobile || this.isMobileLandscape ? this.ratio.landscape : this.ratio.vertical;
      return ratio || 1;
    },
    fontSizeStyle() {
      // Get viewport width
      const viewportWidth = window.innerWidth;

      // Define breakpoints for different screen sizes
      const isSmallDesktop = viewportWidth >= 820 && viewportWidth <= 1440;

      const getDesktopFontSize = (length) => {
        if (isSmallDesktop) {
          if (length >= 5) return 11;
          if (length >= 4) return 12;
          if (length === 3) return 14;
          if (length === 2) return 20;
          return 18;
        } else {
          if (length >= 5) return 13;
          if (length >= 4) return 14;
          if (length === 3) return 16;
          if (length === 2) return 28;
          return 22;
        }
      };

      const getMobileFontSize = (length) => {
        if (length >= 5) return 10;
        if (length >= 4) return 11;
        if (length === 3) return 12;
        if (length === 2) return 14;
        return 15;
      };

      const baseFontSize = isMobile
        ? getMobileFontSize(this.convertedChipValue.length)
        : getDesktopFontSize(this.convertedChipValue.length);

      // Adjust scale factor based on screen size
      let scaleFactor;
      if (isMobile) {
        scaleFactor = this.convertedChipValue.length >= 4 ? this.scaleRatio * 1.2 : this.scaleRatio;
      } else if (isSmallDesktop) {
        scaleFactor = this.scaleRatio * 0.85; // Slightly more reduction for small desktops
      } else {
        scaleFactor = this.scaleRatio * 0.8;
      }

      // Adjust min/max font sizes based on screen size
      const minFontSize = isMobile ? 8 : isSmallDesktop ? 10 : 12;
      const maxFontSize = isMobile ? 15 : isSmallDesktop ? 20 : 24;

      const fontSize = Math.max(mathFloor(baseFontSize / scaleFactor), minFontSize);
      const finalFontSize = Math.min(fontSize, maxFontSize);

      return `f-${finalFontSize}`;
    },
  },
  methods: {
    ...mapActions('bets', ['updateActiveChip']),
    updateActiveChipHandler() {
      this.$emit('updateActiveChip');
      this.updateActiveChip(this.chipValue);
    },
  },
};
</script>
<style lang="scss" scoped>
  .chip-wrapper {
    position: relative;
    min-width: 58px;
    min-height: 58px;
    max-width: 88px;
    display: flex;
    flex: 2;
    box-sizing: border-box;
    padding: 5px;
    cursor: pointer;
    border-radius: 50%;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    border: 1px solid transparent;

    &.disabled {
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
      border-radius: 50%;
      overflow: hidden;
    }

    &.displayOnly {
      pointer-events: none;
    }

    svg,
    img {
      width: 100%;
      height: 100%;
    }

    .chip-value {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-shadow: 0 0 5px rgba(88, 88, 88, 0.9);

      font-weight: 600;
      font-stretch: normal;
      font-style: normal;
      color: #fff;
    }

    &.active,
    &:hover {
      border-radius: 50%;
      border: 1px solid rgba(255, 255, 255, 0.8);
      box-shadow: 0 0 6px 0 #fff;
      box-sizing: border-box;
    }
  }

  @media only screen and (min-width: 992px) {
    .chip-wrapper {
      &.displayOnly {
        min-width: 76px;
        min-height: 76px;
      }

    }
  }
</style>
